import type { ReactElement } from 'react';

interface SparklineProps {
  /** Array de valores. Si es bigint string, parseamos a Number safely. */
  values: ReadonlyArray<number | string>;
  /** width interno del SVG. */
  width?: number;
  /** height interno del SVG. */
  height?: number;
  /** stroke color (CSS var-friendly). */
  color?: string;
  /** opcional: fill area bajo la línea. */
  withArea?: boolean;
  /** className extra para el SVG (usa default `kpi-spark` / `spark`). */
  className?: string;
}

/**
 * Sparkline minimalista renderizada con SVG inline. No tiene ejes,
 * grilla ni labels — sirve sólo para tendencias chiquitas en KPI cards.
 *
 * Si todos los valores son 0 / vacíos, devuelve una línea horizontal
 * fina para mantener la altura del slot.
 */
export function Sparkline({
  values,
  width = 120,
  height = 24,
  color = 'var(--accent)',
  withArea = true,
  className,
}: SparklineProps): ReactElement {
  const numbers = values.map(toNumberSafe);
  const max = Math.max(0, ...numbers);
  const min = Math.min(0, ...numbers);
  const range = Math.max(1, max - min);
  const stepX = numbers.length > 1 ? width / (numbers.length - 1) : width;

  const points = numbers.map((n, i) => {
    const x = i * stepX;
    const y = height - ((n - min) / range) * height;
    return [x, y] as const;
  });

  if (points.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className={className ?? 'spark'}
        aria-hidden="true"
      >
        <line x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} stroke="var(--border-default)" />
      </svg>
    );
  }

  const linePath = points
    .map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`))
    .join(' ');
  const areaPath = `${linePath} L${points[points.length - 1]![0]},${height} L${points[0]![0]},${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={className ?? 'spark'}
      aria-hidden="true"
    >
      {withArea ? <path className="area" d={areaPath} fill={color} fillOpacity={0.15} /> : null}
      <path className="line" d={linePath} stroke={color} strokeWidth={1.2} fill="none" />
    </svg>
  );
}

function toNumberSafe(v: number | string): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  try {
    // bigint string → Number con clamp para evitar NaN.
    if (/^-?\d+$/.test(v)) return Number(BigInt(v));
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}
